import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { sfx } from "@/lib/sfx";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium font-display ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 glow-primary",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-border bg-transparent hover:bg-secondary hover:text-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-secondary hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-primary text-primary-foreground hover:bg-primary/90 glow-primary text-base font-semibold tracking-wide",
        accent: "bg-accent text-accent-foreground hover:bg-accent/90 glow-accent font-semibold",
        glass: "glass-card text-foreground hover:bg-card/80 font-medium",
      },
      size: {
        default: "h-11 px-5 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-13 rounded-xl px-8 py-3 text-base",
        xl: "h-14 rounded-xl px-10 py-4 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Play the generic `tap` SFX on press. Default true.
   *
   * Set `false` whenever the press already OWNS a sound or owns the gesture —
   * never stack two cues on one tap:
   *   - navigation buttons (route change → the `navigate` cue owns it),
   *   - presses that fire `confirm` / `success` / `error` themselves.
   * `link` and `destructive` variants, and `asChild` (renders a Link/anchor →
   * navigation), are auto-suppressed without needing this prop. Toggles use the
   * Switch primitive (its own `toggle` cue), so they never reach here.
   */
  soundOnPress?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, soundOnPress, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    // Default-on for real buttons; auto-suppressed for navigational/textual
    // (`link`), destructive (`confirm` owns it), and `asChild` (Link/anchor →
    // navigation), or by an explicit `soundOnPress={false}`.
    const tapEnabled =
      (soundOnPress ?? true) &&
      !asChild &&
      variant !== "link" &&
      variant !== "destructive";
    const handleClick = tapEnabled
      ? (e: React.MouseEvent<HTMLButtonElement>) => {
          // Engine is gated (vyv_sfx_enabled + reduced-motion), sample-first,
          // best-effort — never throws, never blocks the real handler.
          sfx.tap("module");
          onClick?.(e);
        }
      : onClick;
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
